import { useSelector } from "react-redux";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getOpportunityAccounts, getOpportunityUsers } from "../data/opportunities/opportunitiesSlice";
import { fetchOpportunityById } from "../features/opportunities/data/opportunitiesService";
import { formatNumber } from "../utils/numberUtils";
import { getRegionDefaultValue } from "../utils/regionDefaultValue";
import { plainTextToParagraphHtml } from "../utils/richTextUtils";

import { fetchQuoteTemplate } from "../features/exportTemplates/quote/data/quoteTemplateService";
import { fetchDocumentTemplate } from "../features/exportTemplates/document/data/documentTemplateService";
import { fetchExportTemplate } from "../features/exportTemplates/sheet/data/exportTemplateService";

import { useParams } from "react-router-dom";
import { fetchSingleAccount, fetchSingleOpportunity } from "../features/opportunities/data/opportunitiesService";
import { fetchExportCustomFields } from "../features/adminExports/data/customFieldsService";
import { flattenTemplateFields } from "../features/adminExports/data/customFieldsTree";
import { buildExportDataFromQuoteCustomisation } from "../utils/quoteExportDataUtils";

/**
 * useQuoteRequestData
 *
 * Central data hook that derives everything the quote preview and export pipeline
 * needs from the current quote revision, its items, and the selected template.
 *
 * It is responsible for:
 *  - Flattening upfront/recurring groups and items into ordered, display-ready lists
 *    (`upfrontItems`, `recurringItems`).
 *  - Building the export payload (`exportData`) — hidden items removed, fields
 *    formatted, groups collapsed onto their first child.
 *  - Assembling `customContent` — the merged list of saved field values
 *    (`quoteAdditionalFields`), auto-calculated values (totals, VAT, dates, etc.),
 *    and Salesforce-defaulted values — keyed by the currently selected template's
 *    `customFields` definition.
 *  - Fetching supporting data (Salesforce opportunity, account, all templates) via
 *    React Query, exposing loading/error state for the caller.
 *
 * ─── Parameters ────────────────────────────────────────────────────────────────
 *
 * @param {object}      quoteRevision          - The full quote revision object as
 *   returned by the API (parsed). Must include at minimum:
 *     • `quoteTemplateId`       — used as fallback template lookup on initial load.
 *     • `quoteAdditionalFields` — array of { id, field, value } saved by QuoteContent;
 *       used as the authoritative source of user-entered custom field values.
 *     • `quoteCustomisation`    — groups/items structure for the line editor.
 *     • `revisionNumber`, `quoteId` — used in calculated custom fields.
 *
 * @param {Array}       quoteItems             - Flat array of all quote line items for
 *   this revision. Used to populate group children and compute price totals.
 *
 * @param {object|null} liveQuoteCustomisation - An optional *live* (unsaved) override
 *   for the customisation structure. When provided it takes priority over
 *   `quoteRevision.quoteCustomisation`. Pass `null` (or omit) to use the server value.
 *   Typically provided by ViewQuote when the user is actively reordering groups/items
 *   before the next autosave completes.
 *
 * @param {object|null} selectedTemplate       - The currently selected template object
 *   (as returned by the templates query — same shape as entries in `templatesData`).
 *   This is intentionally a local-state value rather than deriving it from
 *   `quoteRevision.quoteTemplateId`, because `quoteTemplateId` only updates *after* a
 *   save round-trip completes. Passing `selectedTemplate` directly keeps `customContent`
 *   in sync with the UI immediately when the user switches templates, avoiding a window
 *   where the preview renders new-template layout with old-template field values.
 *   Defaults to `null`; the hook falls back to a `templatesData` lookup by
 *   `quoteRevision.quoteTemplateId` when null (e.g. initial page load before
 *   QuoteEditor has populated its local state).
 *
 * ─── Returns ───────────────────────────────────────────────────────────────────
 *
 * @returns {{
 *   salesForceData:        { opportunity, accounts, users },
 *   customContent:         Array<{ id, field, value }>,
 *   exportData:            { upfront: Array, recurring: Array },
 *   upfrontItems:          Array,
 *   recurringItems:        Array,
 *   isLoadingOpportunity:  boolean,
 *   isLoadingTemplates:    boolean,
 *   templatesData:         Array,
 *   templatesError:        Error|null,
 * }}
 *
 * ─── Usage ─────────────────────────────────────────────────────────────────────
 *
 * // In a component that owns selectedTemplate state:
 * const [selectedTemplate, setSelectedTemplate] = useState(null);
 *
 * const {
 *   customContent,
 *   exportData,
 *   upfrontItems,
 *   recurringItems,
 *   isLoadingTemplates,
 *   templatesData,
 * } = useExportRequestData(quoteRevision, quoteItems, quoteCustomisation, selectedTemplate);
 *
 * // Pass selectedTemplate and setSelectedTemplate down to QuoteContent so it can
 * // update the local state synchronously when the user picks a template.  The hook
 * // will immediately recompute customContent for the new template without waiting for
 * // the save round-trip to update quoteRevision.quoteTemplateId.
 */
export const useExportRequestData = (quoteRevision, quoteItems, liveQuoteCustomisation, selectedTemplate = null) => {
    const { id } = useParams();

    const [opportunity, setOpportunity] = useState(null);

    const [upfrontItems, setUpfrontItems] = useState([])
    const [recurringItems, setRecurringItems] = useState([])
    const [exportData, setExportData] = useState([])

    const accountId = opportunity?.Account?.Id ?? null;
    const ownerId = opportunity?.Owner?.Id ?? null;

    const allAccounts = useSelector(getOpportunityAccounts);
    const allUsers = useSelector(getOpportunityUsers);
    const quoteCustomisation = useMemo(() => {
        const qc = liveQuoteCustomisation ?? quoteRevision?.quoteCustomisation ?? {};
        return {
            ...qc,
            groups: {
                upfront: Array.isArray(qc?.groups?.upfront) ? qc.groups.upfront : [],
                recurring: Array.isArray(qc?.groups?.recurring) ? qc.groups.recurring : [],
            },
        };
    }, [liveQuoteCustomisation, quoteRevision?.quoteCustomisation]);

    const getOpportunity = async () => {
        const opportunity = await fetchOpportunityById(quoteRevision?.quote?.salesforceOpportunityId);
        return opportunity?.records;
    }

    const {
        data: opportunityData,
        isLoading: isLoadingOpportunity
    } = useQuery({
        queryKey: ["opportunityData", quoteRevision?.quote?.salesforceOpportunityId],
        queryFn: getOpportunity,
        enabled: !!quoteRevision?.quote?.salesforceOpportunityId,
        staleTime: 5 * 60_000, // 5 minutes
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });

    useEffect(() => {
        if (opportunityData) {
            setOpportunity(opportunityData);
        }
    }, [opportunityData]);

    const { data: opportunityFull } = useQuery({
        queryKey: ["opportunitySingle", id],
        queryFn: () => fetchSingleOpportunity(id),
        staleTime: 60000,
    });

    const { data: accountFull } = useQuery({
        queryKey: ["accountSingle", id],
        queryFn: () => fetchSingleAccount(id),
        staleTime: 60000,
    });    

    const {
        data: templatesData = [],
        isLoading: isLoadingTemplates,
        error: templatesError,
    } = useQuery({
        queryKey: [
            "quoteTemplatesForExport"
        ],
        queryFn: async () => {
            const quoteTemplatesResult = await fetchQuoteTemplate();
            const documentTemplatesResult = await fetchDocumentTemplate();
            const sheetTemplatesResult = await fetchExportTemplate();
            
            const quoteTemplates = Array.isArray(quoteTemplatesResult) ? quoteTemplatesResult : [];
            const documentTemplates = Array.isArray(documentTemplatesResult) ? documentTemplatesResult : [];
            const sheetTemplates = Array.isArray(sheetTemplatesResult) ? sheetTemplatesResult : [];

            return [
                ...quoteTemplates.map(x => ({ ...x, type: "quote" })),
                ...documentTemplates.map(x => ({ ...x, type: "document" })),
                ...sheetTemplates.map(x => ({ ...x, type: "sheet" }))];
        },
        enabled: !!quoteRevision, // wait for quoteRevisionData to be available
        staleTime: 60_000,
    });    

    const { data: globalCustomFields = [] } = useQuery({
        queryKey: ["exportCustomFields"],
        queryFn: fetchExportCustomFields,
        staleTime: 60_000,
    });


    //temporary totals
    const toNumber = (v) => {
        if (typeof v === "number") return Number.isFinite(v) ? v : 0;
        if (typeof v === "string") {
        const n = parseFloat(v.replace(/[^0-9.-]/g, ""));
        return Number.isFinite(n) ? n : 0;
        }
        return 0;
    };

    //temporarily totals ----
    const recTotal = quoteItems.reduce((sum, it) => sum + toNumber(it.totalRecurringPrice), 0);
    const upfTotal = quoteItems.reduce((sum, it) => sum + toNumber(it.totalUpfrontPrice), 0);
    const annualTotal = quoteItems.reduce((sum, it) => sum + toNumber(it.totalAnnualPrice), 0);

    const termLengths = new Set(
        quoteItems.filter(x => 
        x.recurringNumPayments &&
        x.recurringIntervalMonths &&
        x.recurringStartMonth
        ).map(item =>
            (item.recurringNumPayments * item.recurringIntervalMonths) + (item.recurringStartMonth - 1)
        )
    );  

    const VAT_RATE = 0.20;

    const recVAT = recTotal * VAT_RATE;
    const recTotalVAT = recTotal + recVAT;

    const annualVAT = annualTotal * VAT_RATE
    const annualTotalVAT = annualTotal + annualVAT

    const upfVAT = upfTotal * VAT_RATE;
    const upfTotalVAT = upfTotal + upfVAT;
    //temporarily totals end ----


    const calculatedCustomContent = useMemo(() => [        
        {
          id: "ax_1124444333221",
          field: "CUSTOM_TODAY",
          value: new Date().toLocaleDateString("en-GB"),
        },
        {
          field: "CUSTOM_REC_TOTAL",
          id: "ax_rec_total",
          value: formatNumber(recTotal,2)
        },
        {
          field: "CUSTOM_ANNUAL_TOTAL",
          id: "ax_annual_total",
          value: formatNumber(annualTotal,2)
        },
        {
          field: "CUSTOM_REC_VAT",
          id: "ax_rec_vat",
          value: formatNumber(recVAT,2),
        },
        {
          field: "CUSTOM_ANNUAL_VAT",
          id: "ax_annual_vat",
          value: formatNumber(annualVAT,2),
        },
        {
          field: "CUSTOM_REC_TOTAL_VAT",
          id: "ax_rec_total_vat",
          value: formatNumber(recTotalVAT,2),
        },
        {
          field: "CUSTOM_ANNUAL_TOTAL_VAT",
          id: "ax_annual_total_vat",
          value: formatNumber(annualTotalVAT,2),
        },
        {
          field: "CUSTOM_UPF_TOTAL",
          id: "ax_upf_total",
          value: formatNumber(upfTotal,2),
        },
        {
          field: "CUSTOM_UPF_VAT",
          id: "ax_upf_vat",
          value: formatNumber(upfVAT,2),
        },
        {
          field: "CUSTOM_UPF_TOTAL_VAT",
          id: "ax_upf_total_vat",
          value: formatNumber(upfTotalVAT,2),
        },
        {
          field: "CUSTOM_CONTRACT_TOTAL",
          id: "ax_contract_total",
          value: formatNumber(upfTotal + recTotal,2),
        },        
        {
          field: "CUSTOM_CONTRACT_TOTAL_VAT",
          id: "ax_contract_total_vat",
          value: formatNumber(upfTotalVAT + recTotalVAT,2),
        },        
        {
          field: "CUSTOM_TERM_LENGTH",
          id: "ax_contract_total_vat",
          value: formatNumber((termLengths.size > 0 ? Math.max(...termLengths) : 0), 0),
        },    
        {
          field: "CUSTOM_REVISIONNUMBER",
          id: "ax_revision_number",
          value: quoteRevision?.revisionNumber.toString() || ""
        },
        {
          field: "CUSTOM_QUOTENUMBER",
          id: "ax_quote_number",
          value: quoteRevision?.quoteId.toString() || ""
        }
      ]
    , [recTotal, annualTotal, upfTotal, quoteRevision]);   

    const customContent = useMemo(() => {
        if (!templatesData) return [];

        // It is possible for the quoteRevision.quoteTemplateId to be the previously selected template id
        // briefly when switching templates, so we need to guard against that case to avoid picking the wrong 
        // template and then having a mismatch between the custom sections and the content which causes
        // the preview to break.  We do this by passing in the selectedTemplate as an argument to this hook
        // and prioritising it over the quoteRevision.quoteTemplateId when determining the template to use for
        // the custom sections.  This way, when the user selects a new template, the custom sections will update
        // immediately based on the selected template, rather than waiting for the quoteRevision to update which
        // can briefly have the old template id.
        const template = selectedTemplate ?? templatesData.find(
            t => t.type === "quote" && String(t.id) === String(quoteRevision?.quoteTemplateId)
        );

        if (!template || template.customFields === undefined) {
            return [];
        }

        const parsedCustomFields = flattenTemplateFields(template.customFields, template.customSections, template.customFieldsLayout);

        if (!Array.isArray(parsedCustomFields) || parsedCustomFields.length === 0) {
            return [];
        }

        const globalById = new Map();
        (Array.isArray(globalCustomFields) ? globalCustomFields : []).forEach((field) => {
            if (field?.id === undefined || field?.id === null) return;
            globalById.set(String(field.id), field);
        });

        const resolvedTemplateFields = parsedCustomFields
            .map((link, index) => {
                const globalField = globalById.get(String(link?.id));
                const resolvedFieldKey = (
                    globalField?.field ??
                    link?.field ??
                    ""
                ).toString().trim();

                if (!resolvedFieldKey) return null;

                return {
                    ...(globalField || {}),
                    id: globalField?.id ?? link?.id ?? `template-field-${index}`,
                    field: resolvedFieldKey,
                    type: globalField?.type ?? link?.type ?? "Text",
                    defaultValue: globalField?.defaultValue ?? "",
                    salesforceDefaultValue: globalField?.salesforceDefaultValue ?? "",
                    sectionGuid: String(link?.sectionGuid || ""),
                    sortOrder: link?.sortOrder ?? 0,
                };
            })
            .filter(Boolean);

        if (!resolvedTemplateFields.length) {
            return [];
        }

        const allowedFieldKeys = new Set(
            resolvedTemplateFields
                .map((field) => (field?.field || "").toString().trim())
                .filter(Boolean)
        );

        if (!allowedFieldKeys.size) {
            return [];
        }

        const hasAnyContent = value => {
            if (!value) return false;
            const container = document.createElement("div");
            container.innerHTML = value;
            if (container.querySelector("img, table, hr")) return true;
            container.querySelectorAll("br").forEach(lineBreak => lineBreak.remove());
            return (container.textContent || "").length > 0;
        };

        if (resolvedTemplateFields) {
            const sectionsByField = new Map();

            if (Array.isArray(quoteRevision.quoteAdditionalFields)) {
                quoteRevision.quoteAdditionalFields.forEach((qaf) => {
                    if (!qaf?.field) return;
                    if (!allowedFieldKeys.has(qaf.field)) return;

                    sectionsByField.set(qaf.field, {
                        id: qaf.id,
                        field: qaf.field,
                        value: qaf.value,
                    });
                });
            }

            calculatedCustomContent.forEach((cc) => {
                if (!cc?.field) return;
                sectionsByField.set(cc.field, cc);
            });

            resolvedTemplateFields.forEach((element) => {
                let existingEntry = sectionsByField.get(element.field);

                // Fallback: the same field name can exist in both  old and new template (when changing
                // templates) but with different UUIDs (each template creates its own id). If the ID 
                // lookup misses, search quoteAdditionalFields by field name so the saved value is not
                // lost.
                if (!existingEntry && Array.isArray(quoteRevision?.quoteAdditionalFields)) {
                    const priorEntry = quoteRevision.quoteAdditionalFields.find(c => c.field === element.field);
                    if (priorEntry && hasAnyContent(priorEntry.value)) {
                        // Adopt the saved value under the current template's element id so
                        // downstream consumers see the right id.
                        existingEntry = {
                            id: element.id,
                            field: element.field,
                            value: priorEntry.value,
                            sectionGuid: element.sectionGuid,
                        };
                        sectionsByField.set(element.field, existingEntry);
                    }
                }

                if (element.type === "Region") {
                    sectionsByField.set(element.field, {
                        ...(existingEntry && existingEntry.value !== "" ? existingEntry : {
                            id: element.id,
                            field: element.field,
                            value: String(getRegionDefaultValue(
                                element.defaultValue === "" && element.salesforceDefaultValue
                                    ? (opportunityFull?.[element.salesforceDefaultValue.replace("opportunity_", "")] ??
                                       accountFull?.[element.salesforceDefaultValue.replace("account_", "")] ?? "")
                                    : element.defaultValue
                            )),
                        }),
                        sectionGuid: existingEntry?.sectionGuid || element.sectionGuid,
                    });
                    return;
                }

                const defaultValue = element.defaultValue 
                    ? element.defaultValue 
                    : element.salesforceDefaultValue 
                        ? (opportunityFull?.[element.salesforceDefaultValue.replace("opportunity_", "")] ??
                           accountFull?.[element.salesforceDefaultValue.replace("account_", "")] ?? "")
                        : ""

                const formattedDefaultValue =
                    (element.type === "TextArea" || element.type === "TextArea(WYSIWYG)") &&
                    typeof element.salesforceDefaultValue === "string" &&
                    element.salesforceDefaultValue.length > 0
                        ? element.defaultValue
                            ? defaultValue
                            : plainTextToParagraphHtml(defaultValue)
                        : (defaultValue ?? "").toString();

                if (!existingEntry) {
                    sectionsByField.set(element.field, {
                        id: element.id,
                        field: element.field,
                        value: formattedDefaultValue,
                        sectionGuid: element.sectionGuid,
                    });
                } else if (!hasAnyContent(existingEntry.value) && defaultValue !== "") {
                    sectionsByField.set(element.field, {
                        ...existingEntry,
                        value: formattedDefaultValue,
                        sectionGuid: existingEntry.sectionGuid || element.sectionGuid,
                    });
                } else {
                    sectionsByField.set(element.field, {
                        ...existingEntry,
                        sectionGuid: existingEntry?.sectionGuid || element.sectionGuid,
                    });
                }
            });

            return Array.from(sectionsByField.values())
                .filter((s) => allowedFieldKeys.has(s.field))
                .map((s) => ({ ...s, id: s.field }));
        }
        return [];
    }, [
        quoteRevision?.quoteAdditionalFields,
        calculatedCustomContent,
        quoteRevision?.quoteTemplateId,
        templatesData,
        opportunityFull,
        accountFull,
        selectedTemplate,
        globalCustomFields,
    ]);

    useEffect(() => {
        const { upfrontItems, recurringItems, exportData } = buildExportDataFromQuoteCustomisation(
            quoteCustomisation,
            quoteItems
        );

        setUpfrontItems(upfrontItems);
        setRecurringItems(recurringItems);
        setExportData(exportData);
    }, [quoteCustomisation, quoteItems]);

    // NOTE: upfrontItems, recurringItems, and exportData are now all built together in the
    // single effect above via buildExportDataFromQuoteCustomisation. The separate effects
    // that previously called flattenGroupsAndItems directly were removed during extraction.

    const accounts = useMemo(() => {
        if (!accountId || !allAccounts?.length) return [];
        const key = accountId.toString().trim();
        return allAccounts.filter(a => (a.Id ?? a.id)?.toString().trim() === key);
    }, [allAccounts, accountId]);

    const users = useMemo(() => {
        if (!ownerId || !allUsers?.length) return [];
        const key = ownerId.toString().trim();
        return allUsers.filter(u => (u.Id ?? u.id)?.toString().trim() === key);
    }, [allUsers, ownerId]);

    const salesForceData = { opportunity, accounts, users };

    return {
        salesForceData,
        customContent,
        exportData,
        upfrontItems,
        recurringItems,
        isLoadingOpportunity,
        isLoadingTemplates,
        templatesData,
        templatesError
    };
}